import { useState } from "react";

export function useHandleStreamResponse(params = {}) {
  const { onChunk, onFinish, onThreadId, onSuggestedQuestions, onStreamStart, onStreamEnd, onFirstChunk } = params || {};
  const [error, setError] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [completeMessage, setCompleteMessage] = useState("");
  const [questionsSectionFound, setQuestionsSectionFound] = useState(false);
  const [messageBeforeQuestions, setMessageBeforeQuestions] = useState("");

  const handleResponse = async (response, chunkCallback) => {
    const handleChunk = chunkCallback || onChunk;
    const marker = "**You may also want to know about:**";
    let hasReceivedFirstChunk = false;
    let postMarkerBuffer = ""; // Buffer for post-marker content

    try {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      if (onStreamStart) onStreamStart();

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let fullMessage = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        console.debug("Raw chunk:", chunk);
        buffer += chunk;

        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const jsonData = line.substring(6);
            if (jsonData.trim() === "") continue;

            try {
              const parsedData = JSON.parse(jsonData);
              if (parsedData.thread_id) {
                setThreadId(parsedData.thread_id);
                if (onThreadId) onThreadId(parsedData.thread_id);
              }
              if (parsedData.message) {
                if (questionsSectionFound) {
                  postMarkerBuffer += parsedData.message;
                  continue;
                }

                fullMessage += parsedData.message;
                setCompleteMessage(prev => prev + parsedData.message);

                if (!hasReceivedFirstChunk && onFirstChunk) {
                  onFirstChunk();
                  hasReceivedFirstChunk = true;
                }

                if (fullMessage.includes(marker) && !questionsSectionFound) {
                  const markerPosition = fullMessage.indexOf(marker);
                  setQuestionsSectionFound(true);
                  const contentBeforeMarker = fullMessage.substring(0, markerPosition);
                  setMessageBeforeQuestions(contentBeforeMarker);
                  postMarkerBuffer = fullMessage.substring(markerPosition + marker.length);
                  // Send only the new chunk (or pre-marker portion) instead of contentBeforeMarker
                  if (handleChunk) {
                    const cleanChunk = parsedData.message.split(marker)[0];
                    handleChunk(cleanChunk);
                  }
                } else if (handleChunk && !questionsSectionFound) {
                  handleChunk(parsedData.message);
                }
              }
            } catch (e) {
              console.error("Error parsing JSON:", e, "Raw data:", jsonData);
              setError(new Error(`Failed to parse stream data: ${e.message}`));
            }
          }
        }
      }

      if (buffer.startsWith("data: ")) {
        const jsonData = buffer.substring(6);
        if (jsonData.trim() !== "") {
          try {
            const parsedData = JSON.parse(jsonData);
            if (parsedData.thread_id) {
              setThreadId(parsedData.thread_id);
              if (onThreadId) onThreadId(parsedData.thread_id);
            }
            if (parsedData.message) {
              if (questionsSectionFound) {
                postMarkerBuffer += parsedData.message;
              } else {
                fullMessage += parsedData.message;
                setCompleteMessage(prev => prev + parsedData.message);
                if (handleChunk) {
                  handleChunk(parsedData.message);
                }
              }
            }
          } catch (e) {
            console.error("Error parsing JSON (final buffer):", e, "Raw data:", jsonData);
            setError(new Error(`Failed to parse stream data (final): ${e.message}`));
          }
        }
      }

      let finalMessage = fullMessage;
      let questions = [];

      if (questionsSectionFound) {
        finalMessage = messageBeforeQuestions;
        questions = extractSuggestedQuestions(postMarkerBuffer);
      } else {
        const markerPosition = fullMessage.indexOf(marker);
        if (markerPosition !== -1) {
          finalMessage = fullMessage.substring(0, markerPosition);
          questions = extractSuggestedQuestions(fullMessage.substring(markerPosition + marker.length));
        }
      }

      if (questions.length > 0 && onSuggestedQuestions) {
        onSuggestedQuestions(questions);
      }
      if (onFinish) {
        onFinish(finalMessage);
      }

      setQuestionsSectionFound(false);
      setCompleteMessage("");
      setMessageBeforeQuestions("");
      if (onStreamEnd) onStreamEnd();
    } catch (err) {
      setError(err);
      console.error("Error handling stream:", err);
      if (onStreamEnd) onStreamEnd();
      throw err;
    }
  };

  const extractSuggestedQuestions = (content) => {
    if (!content) return [];

    const questions = content
      .split('\n')
      .map(line => {
        const match = line.match(/^\d+\.\s*(.+)$/);
        return match ? match[1].trim() : null;
      })
      .filter(q => q !== null && q.length > 0);

    return questions;
  };

  return { handleResponse, error, threadId };
}